import type { AppVersionEvent } from "./app-version-coordinator";
import { breadcrumb, captureError } from "./observability";

const CAPTURED_EVENTS = new Set<AppVersionEvent>([
  "check_failed",
  "preload_error",
  "reload_blocked",
]);

function eventLevel(event: AppVersionEvent): "info" | "warning" | "error" {
  if (event === "check_failed" || event === "preload_error" || event === "reload_blocked") {
    return "error";
  }
  if (event === "version_mismatch" || event === "reload_deferred_offline") {
    return "warning";
  }
  return "info";
}

function eventOutcome(event: AppVersionEvent, data: Record<string, unknown>): string {
  if (event === "version_current") return "same_version";
  if (event === "version_mismatch") return "update_available";
  if (event === "reload_deferred_offline") return "deferred_offline";
  if (event === "reload_blocked") return "guarded_repeat";
  if (event === "manual_reload") return "manual_reload";
  if (event === "check_failed") return "version_check_failed";
  if (event === "recovery_confirmed") return "recovered_after_reload";
  if (event === "reload_attempted") {
    if (data.trigger === "bootstrap_mismatch") return "bootstrap_reload";
    if (data.trigger === "navigation_mismatch") return "navigation_hard_load";
    return "preload_reload";
  }
  return event;
}

function stringTag(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function reportAppVersionEvent(
  event: AppVersionEvent,
  data: Record<string, unknown>,
  error?: unknown
): void {
  const observedData: Record<string, unknown> = {
    ...data,
    outcome: eventOutcome(event, data),
  };
  breadcrumb(`app_version.${event}`, observedData, {
    category: "app",
    level: eventLevel(event),
  });

  if (!CAPTURED_EVENTS.has(event)) {
    return;
  }

  captureError(error ?? new Error(`Application version recovery: ${event}`), {
    feature: "app-version-recovery",
    operation: event,
    tags: {
      app_version_outcome: stringTag(observedData.outcome),
      app_version_trigger: stringTag(observedData.trigger),
      app_version_route_family: stringTag(observedData.routeFamily),
      app_version_client_release: stringTag(observedData.clientRelease),
      app_version_served_release: stringTag(
        observedData.servedRelease ?? observedData.targetRelease
      ),
    },
    fingerprint: ["app-version-recovery", event],
    extra: observedData,
  });
}
